import type { FilterableHealth, FilterableRow, HealthState, WorkspaceState } from "./types";

export type FilterState = {
  state: WorkspaceState | "all";
  health: HealthState | "all";
  query: string;
};

export const EMPTY_FILTERS: FilterState = { state: "all", health: "all", query: "" };

export const STATE_OPTIONS: Array<{ value: FilterState["state"]; label: string }> = [
  { value: "all", label: "All states" },
  { value: "running", label: "Running" },
  { value: "stopped", label: "Stopped" },
  { value: "orphaned", label: "Orphaned" },
  { value: "terminal", label: "Terminated" },
  { value: "unknown", label: "Unknown" },
];

export const HEALTH_OPTIONS: Array<{ value: FilterState["health"]; label: string }> = [
  { value: "all", label: "Any health" },
  { value: "healthy", label: "Healthy" },
  { value: "unhealthy", label: "Unhealthy" },
  { value: "unknown", label: "Unknown" },
];

/**
 * Builds the projection the filter runs against. Pass every field a search
 * should reach; nullish entries are dropped and the rest is lowercased once.
 */
export function toFilterable(
  state: WorkspaceState,
  health: FilterableHealth,
  searchable: Array<string | null | undefined>,
): FilterableRow {
  return {
    state,
    health,
    haystack: searchable.filter(Boolean).join(" ").toLowerCase(),
  };
}

export function matchesFilters(row: FilterableRow, filters: FilterState): boolean {
  if (filters.state !== "all" && row.state !== filters.state) return false;
  // Rows from providers that do not report health sit out the health filter.
  // Hiding them would empty every non-GCP section the moment a health is picked.
  if (filters.health !== "all" && row.health !== "n/a" && row.health !== filters.health) return false;
  if (filters.query && !row.haystack.includes(filters.query.trim().toLowerCase())) return false;
  return true;
}

/**
 * Filters a list of rows given a projector. Sections keep their own rich row
 * type; only the projection is common.
 */
export function applyFilters<T>(
  rows: T[],
  filters: FilterState,
  project: (row: T) => FilterableRow,
): T[] {
  return rows.filter((row) => matchesFilters(project(row), filters));
}
